import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Model, settings, ChoicesRestfull } from 'survey-core';
import { Survey } from 'survey-react-ui';
import { Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { registerCustomProperties } from '../helpers/customProperties';
import { registerCustomComponents } from '../helpers/registerCustomComponents';
import { registerCustomFunctions } from '../helpers/customFunctions';
import { useAppDispatch, useTypedSelector } from '../../../combineReducers';
import {
  onLoadChoicesFromServer,
  onSurveyComplete,
  onCurrentPageChanged,
  onValueChanged,
  setSurveyInfo,
  onUpdateQuestionCssClasses,
  confirmScopeChange
} from '../helpers/registerSurvayCallbacks';
import { ITaxRate } from '../types/products';
import { fetchOrganizationInfo } from '@pec/aion-ui-core/slices/organizationInfo';
import { TFunction } from 'i18next';
import { pick } from 'lodash';
import { OperationType } from '../types/enums';
import { checkFoundation } from '../helpers/common';
import { InfoDialog } from '../../../components/InfoDialog';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { Navigation } from '../components/Navigation';

registerCustomProperties();
registerCustomComponents();
registerCustomFunctions();

// For subscription field choicesByUrl has optional parameters that are build with expression
settings.webserviceEncodeParameters = false;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      height: '100%',
      padding: theme.spacing(2),
      width: '100%',
      maxWidth: '1200px',
      margin: 'auto'
    },
    headLine: {
      color: 'var(--text-black)',
      fontSize: '1.5rem',
      lineHeight: '1.5rem',
      letterSpacing: '0.01125em',
      marginBottom: theme.spacing(5)
    }
  })
);

type Props = {
  surveyConfig: {
    surveySchema: (
      t: TFunction,
      description: string,
      recurringBillingEnabled: boolean,
      bankTransferEnabled: boolean,
      subscriptionRecurringBillingEnabled: boolean
    ) => any;
    operationType: OperationType;
  };
};
export const Form: React.FC<Props> = ({ surveyConfig }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const classes = useStyles();

  const [survey, setSurvey] = React.useState<Model | null>(null);
  const { organization } = useTypedSelector(state => state.organization);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const { organizationInfo } = useTypedSelector(state => state.organizationInfo);
  const recurringBillingEnabled = organization?.features.includes(OrganizationFeature.RecurringBillingEnabled);
  const bankTransferEnabled = organization?.features.includes(OrganizationFeature.EnabledBankTransfer);
  const formDataChangedRef = React.useRef(false);
  const prevPageVisitedRef = React.useRef(false);
  const taxRatesCacheRef = React.useRef<Record<string, ITaxRate | null>>({});
  const [formState, setFormState] = React.useState<{ isLoading: boolean; error?: string }>({ isLoading: false });
  const [openScopeChangeDialog, setOpenScopeChangeDialog] = React.useState<boolean>(false);
  const initRef = React.useRef(false);

  React.useEffect(() => {
    organization && !organizationInfo && dispatch(fetchOrganizationInfo(organization.id));
  }, [organization, dispatch, organizationInfo]);

  React.useEffect(() => {
    const {
      id,
      description,
      isUpgradeAvailable,
      isRenewalAvailable,
      recurringBillingEnabled: subscriptionRecurringBillingEnabled
    } = mainSubscription;
    const { surveySchema, operationType } = surveyConfig;
    const isFoundation = checkFoundation(description);
    const returnFromUpgrade = operationType === OperationType.Upgrade && !isUpgradeAvailable && !isFoundation;
    const returnFromRenew = operationType === OperationType.Renew && !isRenewalAvailable;
    const isChasGroupContractor =
      (organizationInfo?.isChild || organizationInfo?.isParent) && operationType !== OperationType.ScopeChange;
    const returnFromScopeChange =
      operationType === OperationType.ScopeChange &&
      (isRenewalAvailable || !(description === 'CHAS Standard' || description === 'CHAS Advanced'));

    if (organizationInfo && description && id && organization && !initRef.current) {
      if (returnFromUpgrade || returnFromRenew || isChasGroupContractor || returnFromScopeChange) {
        history.push(`/${organization.id}`);
        return;
      }

      ChoicesRestfull.clearCache();
      const survey = new Model(
        surveySchema(
          t,
          description,
          Boolean(recurringBillingEnabled),
          Boolean(bankTransferEnabled),
          Boolean(subscriptionRecurringBillingEnabled)
        )
      );
      const surveyInitialData = {
        ...pick(organizationInfo, [
          'employeeCount',
          'defaultOperatingCountry',
          'applicationType',
          'assessmentType',
          'companyType',
          'country',
          'countryIso2Code'
        ]),
        organizationId: organization.id,
        email: organization.contactEmailAddress,
        currentSubscription: description,
        subscriptionId: id,
        operationType: surveyConfig.operationType,
        expiryDate: mainSubscription.expirationDate
      };

      setSurveyInfo(survey, surveyInitialData);

      survey.onLoadChoicesFromServer.add(onLoadChoicesFromServer(taxRatesCacheRef));
      survey.onCompleting.add(onSurveyComplete(setFormState, t, formDataChangedRef, setOpenScopeChangeDialog));
      survey.onCurrentPageChanged.add(onCurrentPageChanged(formDataChangedRef, prevPageVisitedRef));
      survey.onValueChanged.add(onValueChanged(formDataChangedRef));
      survey.onUpdateQuestionCssClasses.add(onUpdateQuestionCssClasses);
      setSurvey(survey);
      initRef.current = true;
    }
  }, [
    organization,
    organizationInfo,
    mainSubscription,
    dispatch,
    surveyConfig,
    t,
    history,
    recurringBillingEnabled,
    bankTransferEnabled
  ]);

  if (!survey || formState.isLoading) return <Loading />;
  return (
    <div className={classes.content}>
      <Typography className={classes.headLine} display="block" variant="h6">
        {organization?.name}
      </Typography>
      <Survey model={survey} />
      <Navigation survey={survey} />
      <InfoDialog
        strings={{
          title: t('subscriber:membershipChange.dialog.title', 'Request membership change'),
          content: t(
            'subscriber:membershipChange.dialog.content',
            'After confirmation, our sales team will contact you to assist with your membership update. Please note, changing your membership will invalidate your current certification!'
          ),
          confirm: t('subscriber:membershipChange.dialog.confirm', 'Confirm change'),
          cancel: t('subscriber:membershipChange.dialog.cancel', 'Cancel')
        }}
        open={openScopeChangeDialog}
        onConfirm={() => {
          confirmScopeChange(survey, setFormState, t);
          setOpenScopeChangeDialog(false);
        }}
        onCancel={() => setOpenScopeChangeDialog(false)}
      />
    </div>
  );
};
